import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
} from '@mui/icons-material';
import { getZoomCompensatedSelectMenuProps } from '../utils/zoomCompensation';
import { Event } from '../types';

interface Department {
  id: number;
  department: string;
}

interface EventModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (eventData: {
    title: string;
    description?: string;
    event_date: string;
    affected_department?: number;
  }) => void;
  editingEvent?: Event | null;
  departments: Department[];
}

const EventModal: React.FC<EventModalProps> = ({
  open,
  onClose,
  onSubmit,
  editingEvent = null,
  departments = [],
}) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_date: '',
    affected_department: '',
  });

  const [errors, setErrors] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      if (editingEvent) {
        setFormData({
          title: editingEvent.title,
          description: editingEvent.description || '',
          event_date: editingEvent.event_date,
          affected_department: editingEvent.affected_department?.toString() || '',
        });
      } else {
        setFormData({
          title: '',
          description: '',
          event_date: '',
          affected_department: '',
        });
      }
      setErrors([]);
    }
  }, [open, editingEvent]);

  const handleInputChange = (field: string) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const handleSelectChange = (event: any) => {
    setFormData(prev => ({
      ...prev,
      affected_department: event.target.value,
    }));
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const validateForm = (): string[] => {
    const newErrors: string[] = [];

    if (!formData.title.trim()) {
      newErrors.push('Event title is required');
    }

    if (!formData.event_date) {
      newErrors.push('Event date is required');
    } else {
      const selectedDate = new Date(formData.event_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        newErrors.push('Event date cannot be in the past');
      }
    }

    return newErrors;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors([]);

    try {
      const submissionData = {
        title: formData.title.trim(),
        description: formData.description.trim() || undefined,
        event_date: formData.event_date,
        affected_department: formData.affected_department ? parseInt(formData.affected_department) : undefined,
      };
      await onSubmit(submissionData);
      onClose();
    } catch (error) {
      setErrors([error instanceof Error ? error.message : 'An error occurred while saving the event']);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getMinDate = (): string => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" component="h2">
            {editingEvent ? 'Edit Event' : 'Create New Event'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <form noValidate onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 1 }}>
          {errors.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.map((error, index) => (
                <div key={index}>{error}</div>
              ))}
            </Alert>
          )}

          {/* Event Title */}
          <TextField
            label="Event Title"
            value={formData.title}
            onChange={handleInputChange('title')}
            fullWidth
            required
            error={errors.some((e) => e.toLowerCase().includes('title'))}
            sx={{ mb: 3 }}
            placeholder="Enter event title..."
          />

          {/* Event Date */}
          <TextField
            label="Event Date"
            type="date"
            value={formData.event_date}
            onChange={handleInputChange('event_date')}
            fullWidth
            required
            error={errors.some((e) => e.toLowerCase().includes('date'))}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: getMinDate() }}
            sx={{ mb: 3 }}
          />

          {/* Affected Department */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Affected Department</InputLabel>
            <Select
              value={formData.affected_department}
              label="Affected Department"
              onChange={handleSelectChange}
              MenuProps={getZoomCompensatedSelectMenuProps()}
            >
              <MenuItem value="">
                <em>All Departments</em>
              </MenuItem>
              {departments.map((dept) => (
                <MenuItem key={dept.id} value={dept.id.toString()}>
                  {dept.department}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Event Description */}
          <TextField
            label="Description (Optional)"
            multiline
            rows={3}
            value={formData.description}
            onChange={handleInputChange('description')}
            fullWidth
            placeholder="Add event details or notes..."
            sx={{ mb: 2 }}
          />

          <Typography variant="body2" color="text.secondary">
            This event will send reminders to all users in the selected department (or all departments if none selected) on the event date.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : (editingEvent ? 'Update Event' : 'Create Event')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EventModal;